import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import { Clock, SignOut } from "@phosphor-icons/react";

// Idle auto sign-out is OFF by default. Users opt in from Settings → Session
// (user.auto_signout_minutes = 15/30/60/120); a 2-minute warning modal precedes sign-out.
const WARNING_BEFORE_MS = 2 * 60 * 1000; // show warning 2 min before logout
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];

/**
 * Renders nothing visible until the user has been idle for 28 minutes, at which
 * point a countdown modal appears. If the user doesn't respond within 2 more
 * minutes they are auto-logged-out and shown a toast explaining why.
 *
 * Mounted at App level — noop when the user is anonymous.
 */
export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_BEFORE_MS / 1000));

  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const idleMinutes = Number(user?.auto_signout_minutes || 0);
  const isAuthed = !!(user && user.id) && idleMinutes > 0;
  const IDLE_TIMEOUT_MS = Math.max(idleMinutes, 5) * 60 * 1000;

  const clearAllTimers = useCallback(() => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    warnTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownIntervalRef.current = null;
  }, []);

  const performLogout = useCallback(async () => {
    clearAllTimers();
    setWarningOpen(false);
    try { await logout(); } catch { /* logout clears local state regardless */ }
    toast.warning("You were logged out due to inactivity", {
      description: `You asked us to sign you out after ${idleMinutes} minutes of no activity (Settings → Session). Please sign in again to continue.`,
      duration: 8000,
    });
    navigate("/login", { replace: true });
  }, [logout, navigate, clearAllTimers, idleMinutes]);

  const resetTimers = useCallback(() => {
    if (!isAuthed) return;
    clearAllTimers();
    // Show warning 2 min before logout, i.e. at 28 min of inactivity.
    warnTimerRef.current = setTimeout(() => {
      setSecondsLeft(Math.floor(WARNING_BEFORE_MS / 1000));
      setWarningOpen(true);
      // Live countdown inside the modal
      countdownIntervalRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Auto-logout at the full 30 min
    logoutTimerRef.current = setTimeout(() => {
      performLogout();
    }, IDLE_TIMEOUT_MS);
  }, [isAuthed, clearAllTimers, performLogout, IDLE_TIMEOUT_MS]);

  // Bind activity listeners while authed. Any activity resets the timers —
  // UNLESS the warning modal is already open (user must click "Stay signed in"
  // to dismiss it, so passive motion doesn't accidentally suppress the warning).
  //
  // Ref-based `warningOpen` guard so this effect does NOT re-fire (and reset
  // timers) when the modal opens.
  const warningOpenRef = useRef(false);
  useEffect(() => { warningOpenRef.current = warningOpen; }, [warningOpen]);

  useEffect(() => {
    if (!isAuthed) {
      clearAllTimers();
      setWarningOpen(false);
      return;
    }
    const onActivity = () => {
      if (warningOpenRef.current) return;
      resetTimers();
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    resetTimers();
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      clearAllTimers();
    };
  }, [isAuthed, resetTimers, clearAllTimers]);

  const stayLoggedIn = () => {
    setWarningOpen(false);
    resetTimers();
  };

  const logoutNow = async () => {
    await performLogout();
  };

  if (!isAuthed || !warningOpen) return null;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(1, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="idle-warning-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl border border-black/10">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-11 h-11 rounded-full bg-amber-100 grid place-items-center shrink-0">
            <Clock size={22} weight="bold" className="text-amber-600" />
          </div>
          <div>
            <div className="overline text-amber-600 mb-1">Session about to expire</div>
            <h2 className="font-display font-black text-2xl text-black leading-tight">Still there?</h2>
          </div>
        </div>
        <p className="text-neutral-600 text-sm mb-2">
          You chose to be signed out after {idleMinutes} minutes of inactivity (change this under Settings → Session).
        </p>
        <p className="text-neutral-600 text-sm mb-6">
          You&apos;ll be logged out in{" "}
          <span className="font-mono font-bold text-black tabular-nums" data-testid="idle-countdown">
            {mm}:{ss}
          </span>{" "}
          unless you continue.
        </p>
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button
            onClick={logoutNow}
            data-testid="idle-logout-now"
            className="btn-mfg-outline flex-1 px-4 py-3 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            <SignOut size={14} weight="bold" /> Log out now
          </button>
          <button
            onClick={stayLoggedIn}
            data-testid="idle-stay-signed-in"
            className="btn-mfg-primary flex-1 px-4 py-3 rounded-md text-sm font-semibold"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
